import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import AuthForm from './components/Auth/AuthForm'
import DashboardLayout from './components/Layout/DashboardLayout'
import StudentDashboard from './components/Student/StudentDashboard'
import ParentDashboard from './components/Parent/ParentDashboard'
import ChildProgressView from './components/Parent/ChildProgressView'
import LessonView from './components/Lesson/LessonView'
import LessonAuthor from './components/Lesson/LessonAuthor'
import CurriculumPage from './components/Parent/Curriculum/CurriculumPage'
import CurriculumTemplatesPage from './components/Parent/Curriculum/CurriculumTemplatesPage'
import CurriculumImportExportPage from './components/Parent/Curriculum/CurriculumImportExportPage'
import AssessmentCenter from './components/Lesson/AssessmentCenter'
import Gradebook from './components/Records/Gradebook'
import Attendance from './components/Records/Attendance'
import Transcripts from './components/Records/Transcripts'
import Certificates from './components/Records/Certificates'
import ProtectedRoute from './components/ProtectedRoute'
import ActivityLogs from './components/Features/ActivityLogs'
import FridayShowcase from './components/Features/FridayShowcase'
import EnrichmentBlocks from './components/Features/EnrichmentBlocks'
import FaithGrowth from './components/Features/FaithGrowth'
import ScopeSequencePlanner from './components/Features/ScopeSequencePlanner'
import TeacherHub from './components/Features/TeacherHub'
import AdminPanel from './components/Features/AdminPanel'
import './App.css'

// Dashboard Router Component
const DashboardRouter = () => {
  const { userProfile, userProfileError, loading } = useAuth()

  if (loading) {
    return <div className="text-center py-12">Loading your dashboard...</div>
  }

  if (!userProfile) {
    return (
      <div className="text-center py-12 space-y-2">
        <h2 className="text-xl font-semibold">We couldn&apos;t load your profile.</h2>
        <p className="text-gray-600">Please try signing out and back in or return to the start page.</p>
        <a className="text-[#00AAFF] underline" href="/">Return to start</a>
      </div>
    )
  }

  if (userProfileError) {
    return (
      <div className="text-center py-12 space-y-3">
        <h2 className="text-xl font-semibold">Using a limited profile</h2>
        <p className="text-gray-600">{userProfileError}</p>
        <p className="text-gray-500">Some dashboard features may be unavailable until your profile is restored.</p>
      </div>
    )
  }

  // Route to appropriate dashboard based on role
  const getDashboardComponent = () => {
    switch (userProfile.role) {
      case 'student':
        return <StudentDashboard />
      case 'parent':
        return <ParentDashboard />
      case 'teacher':
        return <TeacherHub />
      case 'administrator':
        return <AdminPanel />
      default:
        return <Navigate to="/" replace />
    }
  }

  return getDashboardComponent()
}

// Main App Component
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App" style={{ fontFamily: 'Roboto, sans-serif' }}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<AuthForm />} />
            
            {/* Protected Routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route path="dashboard" element={<DashboardRouter />} />

              {/* Student Routes */}
              <Route path="classes" element={<div className="text-center py-12"><h2>Classes Coming Soon!</h2></div>} />
              <Route path="progress" element={<div className="text-center py-12"><h2>Progress Coming Soon!</h2></div>} />
              <Route
                path="activity-logs"
                element={
                  <ProtectedRoute allowedRoles={['student', 'parent', 'teacher']}>
                    <ActivityLogs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="friday-showcase"
                element={
                  <ProtectedRoute allowedRoles={['student', 'parent', 'teacher']}>
                    <FridayShowcase />
                  </ProtectedRoute>
                }
              />
              <Route
                path="enrichment-blocks"
                element={
                  <ProtectedRoute allowedRoles={['student', 'parent', 'teacher']}>
                    <EnrichmentBlocks />
                  </ProtectedRoute>
                }
              />
              <Route
                path="faith-growth"
                element={
                  <ProtectedRoute allowedRoles={['student', 'parent', 'teacher']}>
                    <FaithGrowth />
                  </ProtectedRoute>
                }
              />
              <Route
                path="scope-sequence"
                element={
                  <ProtectedRoute allowedRoles={['parent', 'teacher', 'administrator', 'student']}>
                    <ScopeSequencePlanner />
                  </ProtectedRoute>
                }
              />
              <Route path="lesson/:lessonId" element={
                <ProtectedRoute allowedRoles={['student']}>
                  <LessonView />
                </ProtectedRoute>
              } />
              <Route
                path="assessments"
                element={
                  <ProtectedRoute allowedRoles={['parent', 'teacher']}>
                    <AssessmentCenter />
                  </ProtectedRoute>
                }
              />
              <Route
                path="curricula/:curriculumId/courses/:courseId/lessons/new"
                element={
                  <ProtectedRoute allowedRoles={['teacher', 'parent']}>
                    <LessonAuthor />
                  </ProtectedRoute>
                }
              />

              {/* Parent Routes */}
              <Route path="my-children" element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <ParentDashboard />
                </ProtectedRoute>
              } />
              <Route path="child-progress/:childId" element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <ChildProgressView />
                </ProtectedRoute>
              } />
              <Route path="curricula" element={
                <ProtectedRoute allowedRoles={['parent', 'teacher']}>
                  <CurriculumPage />
                </ProtectedRoute>
              } />
              <Route path="curriculum-templates" element={
                <ProtectedRoute allowedRoles={['parent', 'teacher']}>
                  <CurriculumTemplatesPage />
                </ProtectedRoute>
              } />
              <Route path="curriculum-import-export" element={
                <ProtectedRoute allowedRoles={['parent', 'teacher']}>
                  <CurriculumImportExportPage />
                </ProtectedRoute>
              } />
              <Route
                path="gradebook"
                element={
                  <ProtectedRoute allowedRoles={['parent', 'teacher']}>
                    <Gradebook />
                  </ProtectedRoute>
                }
              />
              <Route
                path="attendance"
                element={
                  <ProtectedRoute allowedRoles={['parent', 'teacher']}>
                    <Attendance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="transcripts"
                element={
                  <ProtectedRoute allowedRoles={['parent', 'teacher']}>
                    <Transcripts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="certificates"
                element={
                  <ProtectedRoute allowedRoles={['parent', 'teacher']}>
                    <Certificates />
                  </ProtectedRoute>
                }
              />
              <Route path="settings" element={
                <ProtectedRoute allowedRoles={['parent', 'administrator']}>
                  <div className="text-center py-12"><h2>Settings Coming Soon!</h2></div>
                </ProtectedRoute>
              } />

              {/* Teacher Features */}
              <Route
                path="teacher-hub"
                element={
                  <ProtectedRoute allowedRoles={['teacher']}>
                    <TeacherHub />
                  </ProtectedRoute>
                }
              />

              {/* Admin Panel */}
              <Route
                path="admin-panel"
                element={
                  <ProtectedRoute allowedRoles={['administrator']}>
                    <AdminPanel />
                  </ProtectedRoute>
                }
              />

              {/* Shared Routes */}
              <Route path="profile" element={<div className="text-center py-12"><h2>Profile Coming Soon!</h2></div>} />
            </Route>
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  )
}

export default App
